import React from 'react'
import propTypes from 'prop-types'
import {connect} from 'react-redux'
import {get, isEmpty, isNil} from 'lodash'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Switch from '@material-ui/core/Switch'
import iconNames from 'theme/icons'
import {getTaxCode} from 'containers/partPivotTable'
import PivotTable from 'containers/pivotTable'
import SelectablePivotTable from 'containers/selectablePivotTable'
import FinancialString from 'containers/financialString'
import Translator from "components/translator"
import {QuoteEntity} from 'components/entityObject'
import ActionPhoneLink from 'components/actionPhoneLink'
import ActionEmailLink from 'components/actionEmailLink'
import {getCurrencyPrecision, getRegionalLocaleTaxTerm} from 'selectors/settings'
import {getHomeCurrency, getOrderReasons, getTaxCodes, getUserFields} from 'selectors/data'
import {getUser} from 'selectors/auth'
import {getSelectedCompany, getSelectedContact} from 'selectors/contacts'
import {getHeaderHeight} from 'selectors/ui'
import {getAllSelectedRows} from 'selectors/pivotTable'
import {showAddQuoteDialog} from 'actions/dialog'
import {changeSelectedRows} from 'actions/pivotTable'
import {getFinancialValue, getPercentageString} from 'helpers/financial'
import Locations from 'helpers/locations'
import {prefix} from 'helpers/component'
import {getModuleTop} from 'helpers/ui'
import {permissions, USER_FIELD_TYPES} from 'helpers/constants'
import {getUserFieldPivotTableColumnDefinition} from 'helpers/fields'
import {userHasPermission} from 'helpers/user'
import {getOrderReasonName} from 'helpers/orders'
import {getContactLabel} from 'helpers/contacts'
import {getTaxCodeOption, getTaxRateDisplay} from 'helpers/taxCodes'
import {isProductionEnabled} from "helpers/production"
import {getJobBoardOption} from "helpers/orders"
import {
  applyColumnVisibility,
  createDateColumn,
  createDateTimeColumn,
  getColorStatusOption,
  getTranslatedColumns,
  getTranslatedLabels,
} from 'helpers/pivotTable'
import {
  getAccountManagerName,
  getColorForQuoteStatus,
  getFormattedReferralValue,
  getNameFromObjectForKey,
  getOpportunityName,
  getQuoteStatus,
  getReferralTypeName,
  getRemainingToBeInvoiced,
  OrderReasonType,
} from 'helpers/quotes'
import './quotePivotTable.scss'

const getClassName = prefix('quotePivotTable-')

const singular = 'Quote'
const plural = 'Quotes'

// Fields required in call to quotes API for use in this pivot table
export const quotePivotTableFields = {
  default: true,
  summaryinfo: true,
  company: {
    id: true,
    name: true,
    type: true,
  },
  contact: {
    id: true,
    companyId: true,
    forename: true,
    surname: true,
    mobile: true,
    phone: true,
    email: true,
    location: true,
    accountmanager: true,
  },
  creator: {
    id: true,
    companyId: true,
    forename: true,
    surname: true,
  },
  estimator: {
    id: true,
    companyId: true,
    forename: true,
    surname: true,
  },
  salesman: {
    id: true,
    companyId: true,
    forename: true,
    surname: true,
  },
  opportunity: true,
  campaign: true,
  taxcode: true,
  currency: true,
  orderreason: true,
  rejectreason: true,
  type: true,
  total: true,
  userfields: true,
  referralcompany: {
    name: true,
  },
  totalInvoiced: true,
  overrides: true,
}

export const quoteSelectorPivotTableFields = {
  ...quotePivotTableFields,
  deliveryNotes: {
    default: true,
    deliveryNoteItems: true,
    printOptions: true,
  },
  orderItems: true,
}

export const getColumnDefinition = (props, context) => {
  const {muiTheme} = context
  const {
    user,
    regionalLocaleTaxTerm,
    userFields,
    orderReasons,
    precision,
    homeCurrency,
    taxCodes,
    productionDisabled,
  } = props
  const canViewCosts = userHasPermission(user, permissions.VIEW_QUOTE_COSTS)

  const columns = {
    ref: {
      key: 'ref',
      label: 'Reference',
      size: 100,
      visible: true,
      data: e => e.ref + '/' + e.revision,
      order: 1,
    },
    status: {
      key: 'status',
      label: 'Status',
      size: 150,
      visible: true,
      order: 0,
      data: e => {
        const color = getColorForQuoteStatus(muiTheme, e.status.value)
        return (
          <div className={getClassName('status')}>
            <span className={getClassName('status-color')} style={{backgroundColor: color}}/>
            <span className={getClassName('status-label')}>
                {getQuoteStatus(e.status.value)}
              </span>
          </div>
        )
      },
      export: e => getQuoteStatus(e.status.value),
      group: e => getQuoteStatus(e.status.value),
      groupData: e => getQuoteStatus(e.status.value),
    },
    desc: {
      key: 'desc',
      label: 'Description',
      size: 300,
      visible: true,
      data: e => get(e, 'desc', null),
      order: 2,
    },
    type: {
      key: 'type',
      label: 'Job Type',
      size: 100,
      visible: true,
      data: e => getNameFromObjectForKey(e, 'type'),
      order: 3,
    },
    company: {
      key: 'company',
      label: 'Company',
      size: 200,
      visible: true,
      data: e => get(e, 'company.name', null),
      order: 4,
    },
    contact: {
      key: 'contact',
      label: 'Contact',
      size: 200,
      visible: true,
      data: e => getContactLabel(get(e, 'contact', null)),
      order: 5,
    },
    estimator: {
      key: 'estimator',
      label: 'Estimator',
      size: 200,
      visible: true,
      data: e => getContactLabel(get(e, 'estimator', null)),
      order: 6,
    },
    salesman: {
      key: 'salesman',
      label: 'Salesperson',
      size: 200,
      visible: true,
      data: e => {
        const contact = get(e, 'salesman', null)
        return isNil(contact) ? '' : getContactLabel(contact)
      },
      order: 7,
    },
    reportedPrice: {
      key: 'reportedPrice',
      label: 'Total Value',
      size: 100,
      visible: true,
      align: 'right',
      data: e => <FinancialString value={e.reportedPrice} currencyId={e.currencyId}/>,
      export: e => getFinancialValue(e.reportedPrice, precision),
      group: e => getFinancialValue(e.reportedPrice, precision),
      order: 8,
    },
    totalPrice: {
      key: 'totalPrice',
      label: 'Total Price',
      size: 100,
      visible: false,
      align: 'right',
      data: e => <FinancialString value={e.totalPrice} currencyId={e.currencyId}/>,
      export: e => getFinancialValue(e.totalPrice, precision),
      group: e => getFinancialValue(e.totalPrice, precision),
    },
    profit: {
      key: 'profit',
      label: 'Profit',
      size: 100,
      visible: false,
      align: 'right',
      data: e => !isNil(e.profit) ? <FinancialString value={e.profit} currencyId={e.currencyId}/> : '',
      export: e => getFinancialValue(e.profit, precision),
      group: e => getFinancialValue(e.profit, precision),
    },
    revision: {
      key: 'revision',
      label: 'Revisions',
      size: 100,
      visible: false,
      data: e => get(e, 'revision', null),
    },
    site: {
      key: 'site',
      label: 'Site',
      size: 100,
      visible: false,
      data: e => get(e, 'site', null),
    },
    creator: {
      key: 'creator',
      label: 'Created By',
      size: 200,
      visible: false,
      data: e => {
        const contact = get(e, 'creator', null)
        return isNil(contact) ? '' : getContactLabel(contact)
      },
    },
    accountManager: {
      key: 'accountManager',
      label: 'Account Manager',
      size: 200,
      visible: false,
      data: e => getAccountManagerName(e.contact),
    },
    subTotalPrice: {
      key: 'subTotalPrice',
      label: 'Sub Total',
      size: 100,
      visible: false,
      align: 'right',
      data: e => <FinancialString value={e.subTotalPrice} currencyId={e.currencyId}/>,
      export: e => getFinancialValue(e.subTotalPrice, precision),
      group: e => getFinancialValue(e.subTotalPrice, precision),
    },
    discount: {
      key: 'discount',
      label: 'Discount',
      size: 100,
      visible: false,
      align: 'right',
      data: e => <FinancialString value={e.discount} currencyId={e.currencyId}/>,
      export: e => getFinancialValue(e.discount, precision),
      group: e => getFinancialValue(e.discount, precision),
    },
    discountPc: {
      key: 'discountPc',
      label: 'Discount (%)',
      size: 100,
      visible: false,
      align: 'right',
      data: e => getPercentageString(e.discountPc),
    },

    totalTax: {
      key: 'totalTax',
      label: `Total ${regionalLocaleTaxTerm}`,
      size: 100,
      visible: false,
      align: 'right',
      data: e => <FinancialString value={e.totalTax} currencyId={e.currencyId}/>,
      export: e => getFinancialValue(e.totalTax, precision),
      group: e => getFinancialValue(e.totalTax, precision),
    },
    homeDiscount: {
      key: 'homeDiscount',
      label: 'Home Discount',
      size: 100,
      visible: false,
      align: 'right',
      data: e => <FinancialString value={e.homeDiscount}/>,
      export: e => getFinancialValue(e.homeDiscount, precision),
      group: e => getFinancialValue(e.homeDiscount, precision),
    },
    homePrice: {
      key: 'homePrice',
      label: 'Home Price',
      size: 100,
      visible: false,
      align: 'right',
      data: e => <FinancialString value={e.homePrice}/>,
      export: e => getFinancialValue(e.homePrice, precision),
      group: e => getFinancialValue(e.homePrice, precision),
    },
    homeTax: {
      key: 'homeTax',
      label: `Home ${regionalLocaleTaxTerm}`,
      size: 100,
      visible: false,
      align: 'right',
      data: e => <FinancialString value={e.homeTax}/>,
      export: e => getFinancialValue(e.homeTax, precision),
      group: e => getFinancialValue(e.homeTax, precision),
    },
    margin: {
      key: 'margin',
      label: 'Margin',
      size: 100,
      visible: false,
      align: 'right',
      data: e => getPercentageString(e.margin),
    },
    markup: {
      key: 'markup',
      label: 'Markup',
      size: 100,
      visible: false,
      align: 'right',
      data: e => getPercentageString(e.markup),
    },
    createdDate: createDateTimeColumn({
      key: 'createdDate',
      label: 'Created On',
      size: 150,
      visible: true,
      data: e => get(e, 'createdDate', null),
      order: 8,
    }),
    orderNumber: {
      key: 'orderNumber',
      label: 'Order No',
      size: 100,
      visible: false,
      data: e => get(e, 'orderNumber', null),
    },
    opportunity: {
      key: 'opportunity',
      label: 'Opportunity',
      size: 200,
      visible: false,
      data: e => getOpportunityName(e.opportunity),
    },
    campaign: {
      key: 'campaign',
      label: 'Lead Source',
      size: 100,
      visible: false,
      data: e => get(e, 'campaign.name', ''),
    },
    requiredDate: createDateTimeColumn({
      key: 'requiredDate',
      label: 'Required Date',
      size: 100,
      visible: false,
      data: e => get(e, 'requiredDate', null),
    }),
    quoteRequiredDate: createDateColumn({
      key: 'quoteRequiredDate',
      label: 'Quote Required Date',
      size: 120,
      visible: false,
      data: e => get(e, 'quoteRequiredDate', null),
    }),
    targetDate: createDateTimeColumn({
      key: 'targetDate',
      label: 'Target Completion',
      size: 120,
      visible: false,
      data: e => get(e, 'targetDate', null),
    }),
    plannedStartDate: createDateColumn({
      key: 'plannedStartDate',
      label: 'Works Order Start',
      size: 150,
      visible: false,
      data: e => get(e, 'plannedStartDate', null),
    }),
    probability: {
      key: 'probability',
      label: 'Probability',
      size: 100,
      visible: false,
      align: 'right',
      data: e => getPercentageString(e.probability),
    },
    taxCode: {
      key: 'taxCode',
      label: 'Tax Code',
      size: 100,
      visible: false,
      align: 'right',
      data: e => {
        const taxCodeId = get(e, 'taxcode.id', null)
        const taxCode = getTaxCode(taxCodeId, taxCodes)
        return isNil(taxCode) ? null : get(getTaxCodeOption(taxCode), 'label', null)
      },
    },
    taxRate: {
      key: 'taxRate',
      label: 'Tax Rate',
      size: 100,
      visible: false,
      align: 'right',
      data: e => getTaxRateDisplay(get(e, 'taxRate', 0)) + '%',
    },
    currency: {
      key: 'currency',
      label: 'Currency',
      size: 100,
      visible: false,
      data: e => get(e, 'currency.shortDesc', null),
    },
    currencyRate: {
      key: 'currencyRate',
      label: 'Currency Rate',
      size: 100,
      visible: false,
      align: 'right',
      data: e => get(e, 'currencyRate', null),
    },
    formallyQuoted: {
      key: 'formallyQuoted',
      label: 'Formally Quoted',
      size: 120,
      visible: false,
      data: e => (e.formallyQuoted) ? 'Yes' : 'No',
    },
    grossValue: {
      key: 'grossValue',
      label: 'Gross Value',
      size: 100,
      visible: false,
      align: 'right',
      data: e => <FinancialString value={e.grossValue}/>,
      export: e => getFinancialValue(e.grossValue, precision),
      group: e => getFinancialValue(e.grossValue, precision),
    },
    orderReason: {
      key: 'orderReason',
      label: 'Order Reason',
      size: 100,
      visible: false,
      data: e => getOrderReasonName(e, 'orderReasonId', OrderReasonType.order, orderReasons),
    },
    rejectReason: {
      key: 'rejectReason',
      label: 'Reject Reason',
      size: 100,
      visible: false,
      data: e => getOrderReasonName(e, 'rejectReasonId', OrderReasonType.reject, orderReasons),
    },
    rejectNotes: {
      key: 'rejectNotes',
      label: 'Reject Notes',
      size: 100,
      visible: false,
      data: e => get(e, 'rejectNotes', null),
    },
    notes: {
      key: 'notes',
      label: 'Notes',
      size: 100,
      visible: false,
      data: e => get(e, 'notes', null),
    },
    quotedDate: createDateTimeColumn({
      key: 'quotedDate',
      label: 'Quoted On',
      size: 200,
      visible: false,
      data: e => get(e, 'quotedDate', null),
    }),
    acceptedDate: createDateTimeColumn({
      key: 'acceptedDate',
      label: 'Accepted On',
      size: 200,
      visible: false,
      data: e => get(e, 'acceptedDate', null),
    }),
    expectedDate: createDateColumn({
      key: 'expectedDate',
      label: 'Expected On',
      size: 200,
      visible: false,
      data: e => get(e, 'expectedDate', null),
    }),
    confirmedDate: createDateTimeColumn({
      key: 'confirmedDate',
      label: 'Confirmed On',
      size: 200,
      visible: false,
      data: e => get(e, 'confirmedDate', null),
    }),
    invoicedDate: createDateTimeColumn({
      key: 'invoicedDate',
      label: 'Invoiced On',
      size: 200,
      visible: false,
      data: e => get(e, 'invoicedDate', null),
    }),
    deliveredDate: createDateTimeColumn({
      key: 'deliveredDate',
      label: 'Delivered On',
      size: 200,
      visible: false,
      data: e => get(e, 'deliveredDate', null),
    }),
    paidDate: createDateTimeColumn({
      key: 'paidDate',
      label: 'Paid On',
      size: 200,
      visible: false,
      data: e => get(e, 'paidDate', null),
    }),
    rejectedDate: createDateTimeColumn({
      key: 'rejectedDate',
      label: 'Rejected On',
      size: 200,
      visible: false,
      data: e => get(e, 'rejectedDate', null),
    }),
    modifiedDate: createDateTimeColumn({
      key: 'modifiedDate',
      label: 'Modified On',
      size: 200,
      visible: false,
      data: e => get(e, 'modifiedDate', null),
    }),
    contactEmail: {
      key: 'contactEmail',
      label: 'Email',
      size: 100,
      visible: false,
      data: e => <ActionEmailLink
        value={get(e, 'contact.email', null)}
        category={'Quoting'}/>,
      export: e => get(e, 'contact.email', null),
    },
    contactPhone: {
      key: 'contactPhone',
      label: 'Phone',
      size: 100,
      visible: false,
      data: e => <ActionPhoneLink
        value={get(e, 'contact.phone', null)}
        category={'Quoting'}/>,
      export: e => get(e, 'contact.phone', null),
    },
    contactMobile: {
      key: 'contactMobile',
      label: 'Mobile',
      size: 100,
      visible: false,
      data: e => <ActionPhoneLink
        value={get(e, 'contact.mobile', null)}
        category={'Quoting'}/>,
      export: e => get(e, 'contact.mobile', null),
    },
    contactAddress: {
      key: 'contactAddress',
      label: 'Address',
      size: 200,
      visible: false,
      data: e => e.contact ? Locations.getLocationAddressFromProps(e.contact.location) : '',
    },
    contactCity: {
      key: 'contactCity',
      label: 'City',
      size: 150,
      visible: false,
      data: e => get(e, 'contact.location.city', null),
    },
    contactPostCode: {
      key: 'contactPostCode',
      label: 'Postcode',
      size: 100,
      visible: false,
      data: e => get(e, 'contact.location.postCode', null),
    },
    contactCountry: {
      key: 'contactCountry',
      label: 'Country',
      size: 100,
      visible: false,
      data: e => get(e, 'contact.location.country', null),
    },
    deliveryCity: {
      key: 'deliveryCity',
      label: 'Delivery City',
      size: 150,
      visible: false,
      data: e => get(e, 'overrides.deliveryAddress.city', null),
    },
    deliveryPostCode: {
      key: 'deliveryPostCode',
      label: 'Delivery Post Code',
      size: 200,
      visible: false,
      data: e => get(e, 'overrides.deliveryAddress.postCode', null),
    },
    referralCompany: {
      key: 'referralCompany',
      label: 'Referral Company',
      size: 150,
      visible: true,
      data: e => get(e, 'referralcompany.name', null),
    },
    referralType: {
      key: 'referralType',
      label: 'Referral Type',
      size: 150,
      visible: true,
      data: e => getReferralTypeName(get(e, 'referralType', 0)),
    },
    referralValue: {
      key: 'referralValue',
      label: 'Referral Value',
      size: 150,
      visible: true,
      data: e => getFormattedReferralValue(
        homeCurrency,
        get(e, 'referralValue', null),
        get(e, 'referralType', 0)
      ),
      export: e => get(e, 'referralValue', null),
    },
    totalInvoiced: {
      key: 'totalInvoiced',
      label: 'Invoiced So Far',
      size: 20,
      align: 'right',
      visible: false,
      data: e => <FinancialString value={get(e, 'totalInvoiced')} currencyId={e.currencyId}/>,
      export: e => getFinancialValue(e.totalInvoiced, precision),
      group: e => getFinancialValue(e.totalInvoiced, precision),
      groupData: e => getFinancialValue(e.totalInvoiced, precision),
    },
    remainingInvoiced: {
      key: 'remainingInvoiced',
      label: 'To Be Invoiced',
      size: 20,
      align: 'right',
      visible: false,
      data: e => <FinancialString value={getRemainingToBeInvoiced(e)} currencyId={e.currencyId}/>,
      export: e => getFinancialValue(getRemainingToBeInvoiced(e), precision),
      group: e => getFinancialValue(getRemainingToBeInvoiced(e), precision),
      groupData: e => getFinancialValue(getRemainingToBeInvoiced(e), precision),
    },
  }

  if (canViewCosts) {
    columns.totalCost = {
      key: 'totalCost',
      label: 'Total Cost',
      size: 100,
      visible: false,
      align: 'right',
      data: e => <FinancialString value={e.totalCost}/>,
      export: e => getFinancialValue(e.totalCost, precision),
      group: e => getFinancialValue(e.totalCost, precision),
    }
  }

  if (!productionDisabled) {
    columns.jobBoard = {
      key: 'jobBoard',
      label: 'Job Board',
      size: 200,
      visible: true,
      data: e => {
        const {statusColor, label} = getJobBoardOption(e)
        return getColorStatusOption(statusColor, label)
      },
      export: e => getJobBoardOption(e).label,
      group: e => getJobBoardOption(e).label,
      groupData: e => getJobBoardOption(e).label,
      order: 6,
    }
  }

  userFields.forEach(field => {
    const definition = getUserFieldPivotTableColumnDefinition(user, field, true)
    columns[definition.key] = definition
  })

  return columns
}

const QuotePivotTable = (props, context) => {
  const {
    stateKey,
    filters,
    onUpdateFilters,
    visibleFilters,
    onQuoteSelect,
    headerHeight,
    defaultVisibleColumns,
    showRowSelector,
    selectable,
    selectedRows,
    onChangeSelectedRows,
    onCanSelect,
    ...otherProps
  } = props

  const {showCompleted = true, showInactive = true, showAllRevisions = false} = filters
  const {completed = true, inactive = true, allRevisions = true} = visibleFilters

  const columns = applyColumnVisibility(getColumnDefinition(props, context), defaultVisibleColumns)

  let content = []
  if (onUpdateFilters) {
    if (completed || inactive || allRevisions) content = []
    if (completed) {
      content.push(
        <FormControlLabel
          key={'completed'}
          control={
            <Switch checked={showCompleted} onChange={(e, s) => onUpdateFilters({showCompleted: s})}/>
          }
          label={'Show Paid'}
        />
      )
    }

    if (inactive) {
      content.push(
        <FormControlLabel
          key={'inactive'}
          control={
            <Switch checked={showInactive} onChange={(e, s) => onUpdateFilters({showInactive: s})}/>
          }
          label={'Show Inactive'}
        />
      )
    }

    if (allRevisions) {
      content.push(
        <FormControlLabel
          key={'allRevisions'}
          control={
            <Switch checked={showAllRevisions} onChange={(e, s) => onUpdateFilters({showAllRevisions: s})}/>
          }
          label={'Show All Revisions'}
        />
      )
    }
  }

  const onRender = (translatedLabels) => {
    return (
      <PivotTable
        stateKey={stateKey}
        singularNoun={singular}
        stickyTop={getModuleTop(headerHeight)}
        pluralNoun={plural}
        icon={iconNames.order}
        columns={getTranslatedColumns(columns, translatedLabels)}
        groupingAllowed={true}
        listEntityClass={QuoteEntity}
        onSelectEntity={onQuoteSelect}
        showRowSelector={showRowSelector}
        tightFilters
        {...otherProps}
      >
        {isEmpty(content) ? null : content}
      </PivotTable>
    )
  }

  if (selectable) {
    return (
      <SelectablePivotTable
        stateKey={stateKey}
        singularNoun={singular}
        pluralNoun={plural}
        icon={iconNames.order}
        stickyTop={getModuleTop(headerHeight)}
        columns={columns}
        listEntityClass={QuoteEntity}
        onSelectEntity={onQuoteSelect}
        onCanSelect={onCanSelect}
        canSelectRows={true}
        selectedRows={selectedRows}
        onChangeSelectedRows={onChangeSelectedRows}
        {...otherProps}
      >
        {isEmpty(content) ? null : content}
      </SelectablePivotTable>
    )
  }

  return (
    <Translator text={getTranslatedLabels(columns)} onRender={onRender}/>
  )
}

QuotePivotTable.propTypes = {
  stateKey: propTypes.string.isRequired,
  filters: propTypes.shape({
    showCompleted: propTypes.bool,
    showInactive: propTypes.bool,
    showAllRevisions: propTypes.bool,
  }).isRequired,
  visibleFilters: propTypes.shape({
    completed: propTypes.bool,
    inactive: propTypes.bool,
    allRevisions: propTypes.bool,
  }).isRequired,
  regionalLocaleTaxTerm: propTypes.string.isRequired,
  userFields: propTypes.array,
  precision: propTypes.number,
  company: propTypes.object,
  contact: propTypes.object,
  opportunity: propTypes.object,
  headerHeight: propTypes.number,
  defaultVisibleColumns: propTypes.arrayOf(propTypes.string),
  onUpdateFilters: propTypes.func,
  onQuoteSelect: propTypes.func,
  onAdd: propTypes.func,
  onBulkRemove: propTypes.func,
  showRowSelector: propTypes.bool,
  productionDisabled: propTypes.bool,
  selectable: propTypes.bool,
  selectedRows: propTypes.array,
  onChangeSelectedRows: propTypes.func,
  onCanSelect: propTypes.func,
}

QuotePivotTable.contextTypes = {
  muiTheme: propTypes.object.isRequired,
}

QuotePivotTable.defaultProps = {
  defaultVisibleColumns: [
    'ref',
    'status',
    'desc',
    'type',
    'company',
    'contact',
    'estimator',
    'salesman',
    'reportedPrice',
  ],
  showRowSelector: true,
  selectable: false,
}

const mapStateToProps = (state, ownProps) => {
  const user = getUser(state)
  return {
    user,
    currentCompany: getSelectedCompany(state),
    currentContact: getSelectedContact(state),
    headerHeight: getHeaderHeight(state),
    regionalLocaleTaxTerm: getRegionalLocaleTaxTerm(state),
    userFields: getUserFields(state, USER_FIELD_TYPES.QUOTE_USER_FIELD),
    orderReasons: getOrderReasons(state),
    precision: getCurrencyPrecision(state),
    homeCurrency: getHomeCurrency(state),
    taxCodes: getTaxCodes(state),
    productionDisabled: !isProductionEnabled(user),
    selectedRows: getAllSelectedRows(state, ownProps.stateKey),
  }
}

const mapDispatchToProps = (dispatch, ownProps) => {
  return {
    onChangeSelectedRows: (selectedIds) => dispatch(changeSelectedRows(ownProps.stateKey, selectedIds)),
    onAdd: ownProps.onAdd || ((company, contact, opportunity) => {
      dispatch(showAddQuoteDialog({
        company: ownProps.currentCompany || company,
        contact: ownProps.currentContact || contact,
        opportunity: ownProps.currentOpportunity || opportunity,
      }))
    }),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(QuotePivotTable)
